import { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    Stack,
    Step,
    StepLabel,
    Stepper,
    Typography,
} from '@mui/material';
import CloudIcon from '@mui/icons-material/Cloud';
import {
    discoverSources,
    getAuthorizeUrl,
    getConnection,
    listSources,
    startSync,
    type CloudSyncConnection,
    type CloudSyncProvider,
    CLOUD_SYNC_PROVIDERS,
} from '@wwf/sdk';
import { DialogCloseButton } from '@app/misc/DialogCloseButton';
import { DrawerState, useDrawer } from '@app/misc/DrawerContext';
import {
    isActiveConnection,
    PROVIDER_DESCRIPTIONS,
    PROVIDER_LABELS,
} from './cloudSyncConstants';

const WIZARD_STEPS = ['Provider', 'Connect', 'Discover', 'Sync'] as const;

function computeStep(
    provider: CloudSyncProvider | null,
    connection: CloudSyncConnection | null,
    sourceCount: number
): number {
    if (!provider) {
        return 0;
    }
    if (
        !isActiveConnection(connection) ||
        connection?.status === 'NEEDS_RECONNECT'
    ) {
        return 1;
    }
    if (sourceCount === 0) {
        return 2;
    }
    return 3;
}

export function useCloudSyncSetupDrawer(onUpdated?: () => Promise<void>) {
    const drawer = useDrawer();
    const openSetupDrawer = useCallback(
        (options?: { provider?: CloudSyncProvider }) => {
            drawer.openDrawer(state => (
                <CloudSyncSetupDrawer
                    {...state}
                    initialProvider={options?.provider}
                    onUpdated={onUpdated}
                />
            ));
        },
        [drawer, onUpdated]
    );
    return { openSetupDrawer };
}

export function CloudSyncSetupDrawer({
    open,
    onClose,
    onUpdated,
    initialProvider,
}: DrawerState & {
    onUpdated?: () => Promise<void>;
    initialProvider?: CloudSyncProvider;
}) {
    const [step, setStep] = useState(0);
    const [selectedProvider, setSelectedProvider] =
        useState<CloudSyncProvider | null>(null);
    const [lockedProvider, setLockedProvider] =
        useState<CloudSyncProvider | null>(null);
    const [connection, setConnection] = useState<CloudSyncConnection | null>(
        null
    );
    const [sourceCount, setSourceCount] = useState(0);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) {
            return;
        }

        let cancelled = false;

        const loadWizardState = async () => {
            setError(null);
            setBusy(true);
            try {
                let activeProvider: CloudSyncProvider | null = null;
                let activeConnection: CloudSyncConnection | null = null;

                for (const provider of CLOUD_SYNC_PROVIDERS) {
                    const providerConnection = await getConnection(provider);
                    if (isActiveConnection(providerConnection)) {
                        activeProvider = provider;
                        activeConnection = providerConnection;
                        break;
                    }
                }

                const provider = initialProvider ?? activeProvider ?? null;

                let resolvedConnection = activeConnection;
                if (provider && provider !== activeProvider) {
                    resolvedConnection = await getConnection(provider);
                }

                const sources =
                    provider && isActiveConnection(resolvedConnection)
                        ? await listSources(provider)
                        : [];

                if (cancelled) {
                    return;
                }

                setLockedProvider(activeProvider);
                setSelectedProvider(provider);
                setConnection(resolvedConnection);
                setSourceCount(sources.length);
                setStep(
                    computeStep(provider, resolvedConnection, sources.length)
                );
            } catch (e) {
                if (!cancelled) {
                    setError(
                        e instanceof Error
                            ? e.message
                            : 'Failed to load setup wizard'
                    );
                }
            } finally {
                if (!cancelled) {
                    setBusy(false);
                }
            }
        };

        void loadWizardState();

        return () => {
            cancelled = true;
        };
    }, [open, initialProvider]);

    const handleConnect = async () => {
        if (!selectedProvider) {
            return;
        }
        const url = await getAuthorizeUrl(selectedProvider);
        window.location.href = url;
    };

    const handleDiscover = async () => {
        if (!selectedProvider) {
            return;
        }
        setBusy(true);
        setError(null);
        try {
            const sources = await discoverSources(selectedProvider);
            setSourceCount(sources.length);
            setStep(3);
            await onUpdated?.();
        } catch (e) {
            setError(
                e instanceof Error ? e.message : 'Failed to discover sources'
            );
        } finally {
            setBusy(false);
        }
    };

    const handleStartSync = async () => {
        if (!selectedProvider) {
            return;
        }
        setBusy(true);
        setError(null);
        try {
            await startSync(selectedProvider);
            await onUpdated?.();
            onClose();
        } catch (e) {
            setError(
                e instanceof Error ? e.message : 'Failed to start initial sync'
            );
        } finally {
            setBusy(false);
        }
    };

    const canSelectProvider = (provider: CloudSyncProvider) =>
        !lockedProvider || lockedProvider === provider;

    const canGoNext =
        step === 0
            ? selectedProvider !== null
            : step === 1
              ? isActiveConnection(connection)
              : step === 2
                ? sourceCount > 0
                : false;

    const handleNext = () => {
        if (step === 0 && selectedProvider) {
            setStep(1);
            void getConnection(selectedProvider).then(setConnection);
            return;
        }
        if (step === 1 && isActiveConnection(connection)) {
            setStep(2);
            return;
        }
        if (step === 2 && sourceCount > 0) {
            setStep(3);
        }
    };

    const handleBack = () => {
        if (step === 0) {
            return;
        }
        if (step === 1 && lockedProvider) {
            return;
        }
        setStep(current => Math.max(0, current - 1));
    };

    return (
        <Box sx={{ position: 'relative', height: '100%' }}>
            <DialogCloseButton onClose={onClose} />
            <Box
                p={3}
                pt={4}
                display="flex"
                flexDirection="column"
                height="100%"
            >
                <Typography variant="h6" gutterBottom>
                    Cloud sync setup
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                    Connect one cloud storage provider for document
                    synchronisation.
                </Typography>

                <Stepper
                    activeStep={step}
                    orientation="vertical"
                    sx={{ flex: 1 }}
                >
                    <Step>
                        <StepLabel>Choose provider</StepLabel>
                    </Step>
                    <Step>
                        <StepLabel>Connect account</StepLabel>
                    </Step>
                    <Step>
                        <StepLabel>Discover sources</StepLabel>
                    </Step>
                    <Step>
                        <StepLabel>Start sync</StepLabel>
                    </Step>
                </Stepper>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Box sx={{ flex: 1, mt: 2 }}>
                    {step === 0 && (
                        <Stack spacing={1.5}>
                            {CLOUD_SYNC_PROVIDERS.map(provider => {
                                const selectable = canSelectProvider(provider);
                                const selected = selectedProvider === provider;
                                return (
                                    <Card
                                        key={provider}
                                        variant="outlined"
                                        sx={{
                                            borderColor: selected
                                                ? 'primary.main'
                                                : 'divider',
                                            opacity: selectable ? 1 : 0.55,
                                        }}
                                    >
                                        <CardActionArea
                                            disabled={!selectable || busy}
                                            onClick={() =>
                                                setSelectedProvider(provider)
                                            }
                                        >
                                            <CardContent>
                                                <Stack
                                                    direction="row"
                                                    spacing={1.5}
                                                    alignItems="center"
                                                >
                                                    <CloudIcon color="action" />
                                                    <Box>
                                                        <Typography variant="subtitle1">
                                                            {
                                                                PROVIDER_LABELS[
                                                                    provider
                                                                ]
                                                            }
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                        >
                                                            {
                                                                PROVIDER_DESCRIPTIONS[
                                                                    provider
                                                                ]
                                                            }
                                                        </Typography>
                                                        {!selectable && (
                                                            <Typography
                                                                variant="caption"
                                                                color="warning.main"
                                                            >
                                                                {
                                                                    PROVIDER_LABELS[
                                                                        lockedProvider!
                                                                    ]
                                                                }{' '}
                                                                is already
                                                                connected.
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Stack>
                                            </CardContent>
                                        </CardActionArea>
                                    </Card>
                                );
                            })}
                        </Stack>
                    )}

                    {step === 1 && selectedProvider && (
                        <Stack spacing={2}>
                            <Typography variant="body2">
                                Sign in with your{' '}
                                {PROVIDER_LABELS[selectedProvider]} account to
                                authorise access.
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Status: {connection?.status ?? 'DISCONNECTED'}
                            </Typography>
                            {connection?.accountEmail && (
                                <Typography variant="body2">
                                    Account: {connection.accountEmail}
                                </Typography>
                            )}
                            {connection?.status === 'NEEDS_RECONNECT' &&
                                connection.lastAuthError && (
                                    <Alert severity="warning">
                                        {connection.lastAuthError}
                                    </Alert>
                                )}
                            <Button
                                variant="contained"
                                onClick={handleConnect}
                                disabled={busy}
                            >
                                {connection?.status === 'NEEDS_RECONNECT'
                                    ? `Reconnect ${PROVIDER_LABELS[selectedProvider]}`
                                    : `Connect ${PROVIDER_LABELS[selectedProvider]}`}
                            </Button>
                        </Stack>
                    )}

                    {step === 2 && selectedProvider && (
                        <Stack spacing={2}>
                            <Typography variant="body2">
                                Scan {PROVIDER_LABELS[selectedProvider]} for
                                folders and libraries to sync.
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {sourceCount > 0
                                    ? `${sourceCount} source(s) found.`
                                    : 'No sources discovered yet.'}
                            </Typography>
                            <Button
                                variant="contained"
                                onClick={handleDiscover}
                                disabled={busy}
                            >
                                Discover sources
                            </Button>
                        </Stack>
                    )}

                    {step === 3 && selectedProvider && (
                        <Stack spacing={2}>
                            <Typography variant="body2">
                                Start the initial import from{' '}
                                {PROVIDER_LABELS[selectedProvider]}. Only new
                                changes will be processed afterwards.
                            </Typography>
                            <Button
                                variant="contained"
                                onClick={handleStartSync}
                                disabled={busy}
                            >
                                Start initial sync
                            </Button>
                        </Stack>
                    )}
                </Box>

                <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="space-between"
                    mt={2}
                >
                    <Button
                        onClick={handleBack}
                        disabled={
                            step === 0 ||
                            (step === 1 && !!lockedProvider) ||
                            busy
                        }
                    >
                        Back
                    </Button>
                    {step < WIZARD_STEPS.length - 1 ? (
                        <Button
                            variant="contained"
                            onClick={handleNext}
                            disabled={!canGoNext || busy}
                        >
                            Next
                        </Button>
                    ) : (
                        <Button onClick={onClose} disabled={busy}>
                            Close
                        </Button>
                    )}
                </Stack>
            </Box>
        </Box>
    );
}
